import { MICRO_PER_CREDIT } from '../../../composables/useCreditWallet'

/**
 * Pure pricing math shared by the staff credit-pricing calculator (Calculadora tab) and
 * the credit-pack catalogue (Pacotes tab) on StaffCreditsPage.
 *
 * This file is the SINGLE source of truth for the §2.4 margin identity:
 *
 *     margin_multiple = retail_price_per_credit_usd / peg_usd
 *
 * Both tabs MUST call computeMarginIdentity() below for that number — never re-derive it
 * locally, or the two views can disagree about the same pack.
 *
 * Dollar-denominated by design: the provider COGS and the peg are both natively USD, so
 * working in dollars sidesteps the fx/peg/markup tangle. `markup`, `peg_brl` and
 * `fx_rate_usd_brl` are DORMANT since M3 (priceUsage() no longer reads them, M6 drops the
 * columns) — this file builds nothing on them. peg_usd is always the caller-supplied value
 * read from AiCredits_AdminOverviewResponseDados.peg_usd (the sole accessor is
 * Ai_credit_pricing_model::getGlobalPegUsd() on the backend) — never hardcoded here, never
 * read a second way.
 *
 * No Vue, no network — callers own rendering; this owns the arithmetic so it is
 * unit-testable in isolation.
 */

// =============================================================================
// §2.4 margin identity — shared export, consumed by the Calculadora tab AND the
// Pacotes tab (per-pack margin display).
// =============================================================================

export interface MarginIdentityInput {
  /** Total retail price of the pack, in USD. */
  retailPriceUsd: number
  /** Pack size, in CREDITS (not micro-credits). */
  packSizeCredits: number
  /** Global peg, USD per credit — AiCredits_AdminOverviewResponseDados.peg_usd, parsed. */
  pegUsd: number
}

export interface MarginIdentityResult {
  /** retailPriceUsd / packSizeCredits. 0 when packSizeCredits is not positive. */
  retailPricePerCreditUsd: number
  /** retailPricePerCreditUsd / pegUsd. Null when pegUsd is not positive (misconfigured/unloaded). */
  marginMultiple: number | null
  grossMarginPerCreditUsd: number
  /** 0..100; 0 when retailPricePerCreditUsd is 0. */
  marginPct: number
}

/**
 * THE §2.4 identity. One function, one source of truth — see file header.
 */
export function computeMarginIdentity(input: MarginIdentityInput): MarginIdentityResult {
  const { retailPriceUsd, packSizeCredits, pegUsd } = input

  const retailPricePerCreditUsd = packSizeCredits > 0 ? retailPriceUsd / packSizeCredits : 0
  const peg = pegUsd > 0 ? pegUsd : 0
  const marginMultiple = peg > 0 ? retailPricePerCreditUsd / peg : null
  const grossMarginPerCreditUsd = retailPricePerCreditUsd - peg
  const marginPct =
    retailPricePerCreditUsd > 0 ? (grossMarginPerCreditUsd / retailPricePerCreditUsd) * 100 : 0

  return { retailPricePerCreditUsd, marginMultiple, grossMarginPerCreditUsd, marginPct }
}

/**
 * Shared display formatter for marginMultiple — used by BOTH the Calculadora tile and
 * the Pacotes column so the same pack reads the same way in both tabs. '—' for null
 * (pegUsd not positive / misconfigured), otherwise one decimal place with the × suffix.
 */
export function formatMarginMultiple(marginMultiple: number | null): string {
  return marginMultiple === null ? '—' : marginMultiple.toFixed(1) + '×'
}

// =============================================================================
// Usage basis — real measured avg credits/prompt vs. the thin-sample fallback, and the
// resulting USD cost per prompt at the peg anchor (§2.5). Calculator-only: this is about
// what a "typical prompt" costs given real usage, not part of the pack-size-driven margin
// identity above.
// =============================================================================

/** Minimum measured prompts before we trust the period's own average over the fallback. */
export const MIN_PROMPTS_FOR_REAL_BASIS = 3

/** Fallback assumption for a thin/empty sample: a typical prompt uses ~1 credit. */
export const FALLBACK_CREDITS_PER_PROMPT = 1

/** Period aggregates lifted from AiCredits_AdminOverviewResponse (real, measured usage). */
export interface CreditPricingSource {
  /** Total micro-credits consumed in the period (settle + meter rows). */
  totalMicro: number
  /** Number of measured prompts in the period (settle + meter rows). */
  settleCount: number
}

export interface UsageBasisResult {
  /** 'real' when derived from measured usage; 'fallback' when the sample was too thin. */
  basis: 'real' | 'fallback'
  avgCreditsPerPrompt: number
  /** avgCreditsPerPrompt * pegUsd — the §2.5 anchor cost. Never the noisy measured COGS. */
  cogsUsdPerPrompt: number
}

/**
 * Derive the usage-basis picture from measured period usage. Falls back to
 * FALLBACK_CREDITS_PER_PROMPT (and reports basis='fallback') when the sample is too thin
 * to trust. Cost is always anchored to the supplied pegUsd — never a hardcoded constant.
 */
export function computeUsageBasis(source: CreditPricingSource | null, pegUsd: number): UsageBasisResult {
  const hasRealData =
    source !== null && source.settleCount >= MIN_PROMPTS_FOR_REAL_BASIS && source.totalMicro > 0

  const avgCreditsPerPrompt = hasRealData
    ? source!.totalMicro / source!.settleCount / MICRO_PER_CREDIT
    : FALLBACK_CREDITS_PER_PROMPT

  const peg = pegUsd > 0 ? pegUsd : 0
  const cogsUsdPerPrompt = avgCreditsPerPrompt * peg

  return { basis: hasRealData ? 'real' : 'fallback', avgCreditsPerPrompt, cogsUsdPerPrompt }
}
